import { Pipe, PipeTransform } from '@angular/core';
import { PaisDDI } from '../models/cardapio-digital/cardapio-digital.model';
import { PAISES_DDI } from '../shared/data/paises-ddi.data';

/**
 * Pipe para formatar telefone com bandeira e DDI.
 *
 * Uso: {{ pessoa | formatPhone }}
 *
 * Recebe um objeto com propriedades `ddi` (string sem "+", ex: "595") e `telefone` (string com digitos).
 * Retorna: "bandeira +DDI telefone_formatado" (ex: "🇵🇾 +595 971 123 456")
 *
 * Se nao tiver ddi ou telefone, retorna string vazia.
 */
@Pipe({
  name: 'formatPhone'
})
export class FormatPhonePipe implements PipeTransform {

  transform(value: any): string {
    if (!value) return '';


    const ddiRaw: string = value.ddi || '';
    const telefoneRaw: string = value.telefone || '';

    if (!ddiRaw && !telefoneRaw) return '';

    // Se nao tem DDI mas tem telefone, tenta inferir DDI pelo prefixo
    if (!ddiRaw && telefoneRaw) {
      const paisInferido = this.inferirPaisPorPrefixo(telefoneRaw.replace(/\D/g, ''));
      if (paisInferido) {
        const digitosSemDDI = telefoneRaw.replace(/\D/g, '').substring(paisInferido.ddi.replace('+', '').length);
        const mascara = this.selecionarMascara(paisInferido, digitosSemDDI);
        const telefoneFormatado = this.applyMask(digitosSemDDI, mascara);
        return `${paisInferido.bandeira} ${paisInferido.ddi} ${telefoneFormatado}`;
      }
      return telefoneRaw;
    }

    // Encontrar o pais pelo DDI
    const ddiComPlus = ddiRaw.startsWith('+') ? ddiRaw : `+${ddiRaw}`;
    const pais: PaisDDI | undefined = PAISES_DDI.find(p => p.ddi === ddiComPlus);

    // Se nao encontrou pais, retorna DDI + telefone sem formatacao
    if (!pais) {
      return telefoneRaw ? `${ddiComPlus} ${telefoneRaw}` : ddiComPlus;
    }

    // Se nao tem telefone, retorna apenas bandeira + DDI
    if (!telefoneRaw) {
      return `${pais.bandeira} ${pais.ddi}`;
    }

    // Extrair apenas digitos do telefone
    const digitos = telefoneRaw.replace(/\D/g, '');

    // Selecionar mascara adequada (curta para fixo BR, padrao para celular)
    const mascara = this.selecionarMascara(pais, digitos);
    const telefoneFormatado = this.applyMask(digitos, mascara);

    return `${pais.bandeira} ${pais.ddi} ${telefoneFormatado}`;
  }

  /**
   * Tenta inferir o pais pelo prefixo numerico do telefone.
   * Ordena por DDI mais longo primeiro para evitar match parcial (ex: "+595" antes de "+59").
   */
  private inferirPaisPorPrefixo(digitosTelefone: string): PaisDDI | undefined {
    const paisesOrdenados = [...PAISES_DDI].sort(
      (a, b) => b.ddi.replace('+', '').length - a.ddi.replace('+', '').length
    );
    return paisesOrdenados.find(p => digitosTelefone.startsWith(p.ddi.replace('+', '')));
  }

  /** Seleciona mascara adequada baseada na quantidade de digitos */
  private selecionarMascara(pais: PaisDDI, digitos: string): string {
    if (pais.mascaraCurta && pais.limiteDigitosCurta && digitos.length <= pais.limiteDigitosCurta) {
      return pais.mascaraCurta;
    }
    return pais.mascara;
  }

  /** Aplica mascara de telefone onde # = digito */
  private applyMask(digitos: string, mascara: string): string {
    let resultado = '';
    let indexDigito = 0;

    for (let i = 0; i < mascara.length && indexDigito < digitos.length; i++) {
      if (mascara[i] === '#') {
        resultado += digitos[indexDigito];
        indexDigito++;
      } else {
        resultado += mascara[i];
      }
    }

    return resultado;
  }
}
